import { AsyncPageRender } from '@components/AsyncComponent';
import ErrorPage from '@containers/Errors/ErrorPage';
import { useUser } from '@hooks/useUser';
import { HTTPCodes } from '@lib/httpCodes';
import { createMemo } from '@packages/react';
import React from 'react';

type Module = null | ((any) => any);

interface UserViews {
    admin?: Module;
    default?: Module;
    salesman?: Module;
    designer?: Module;
    customer?: Module;
    wholesaler?: Module;
    factory?: Module;
}

/**
 * Show right view based on current user
 * @example:
 * // Using react elements
 * const ConditionedView = createUserViews({
 *     customer: ( <div> <h1>Hello</h1> </div>)
 * });
 *
 * // Components
 * const CustomerView = ({name, ...props}) => ( ( <div {...props}> <h1>Hello <i>{name}</i></h1> </div>) )
 * const ConditionedView = createUserViews({
 *     customer: Customer
 * });
 *
 * // Module
 * const ConditionedView = createUserViews({
 *     customer: require('./customerView')
 * });
 *
 * // Promise
 * const ConditionedView = createUserViews({
 *     customer: import('./customerView') // import return a promise
 * });
 *
 * // Async Pages
 * const ConditionedView = createUserViews({
 *     customer: () => import('./customerView')
 * });
 *
 * @param views
 * @param code
 * @returns {*}
 */
export function createUserViews(views: UserViews, code: number = HTTPCodes.HTTP_FORBIDDEN) {
    return createMemo(
        (props: any) => {
            const user = useUser();
            const userView = user?.user_view || 'default';

            const hasView = views && views.hasOwnProperty(userView);
            const hasDefaultView = views && views.hasOwnProperty('default');

            const view$ = hasView ? views[userView] : hasDefaultView ? views.default : undefined;
            const view = view$?.default || view$;

            if (typeof view !== 'undefined') {
                if (React.isValidElement(view)) {
                    return view;
                }

                return AsyncPageRender(view, props);
            }

            return code ? <ErrorPage code={code} /> : null;
        },
        { displayName: 'createUserViews(ViewComponent)' },
    );
}
